import _ from 'lodash';
import React from 'react';
import constants from '#packages/constants';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import * as stateManagement from '#packages/stateManagement';
import mobileWizardFirstStep from './mobileWizardFirstStep';
import mobileWizardSelectTinyMenu from './mobileWizardSelectTinyMenu';
import mobileWizardSelectHeader from './mobileWizardSelectHeader';
import mobileWizardSelectQab from './mobileWizardSelectQab';
import mobileWizardSelectBackToTop from './mobileWizardSelectBackToTop';
import mobileWizardLastStep from './mobileWizardLastStep';
import { Button, Composites } from '@wix/wix-base-ui';
import * as symbols from '@wix/santa-editor-symbols';
import ProgressBar from './progressBar';
import { browserUtil, cx } from '#packages/util';
import styles from './mobileWizard.scss';

import type { Dispatch, StateMapperArgs } from 'types/redux';

const MOBILE_WIZARD_OPENING_STATE =
  constants.MOBILE.MOBILE_WIZARD.OPENING_STATE;
const selectors = stateManagement.mobile.selectors.mobileWizard;
const actions = stateManagement.mobile.actions.mobileWizard;

const targetComponents = {
  MOBILE_WIZARD_FIRST_STEP: mobileWizardFirstStep,
  TINY_MENU: mobileWizardSelectTinyMenu,
  SITE_HEADER: mobileWizardSelectHeader,
  QUICK_ACTION_BAR: mobileWizardSelectQab,
  BACK_TO_TOP_BUTTON: mobileWizardSelectBackToTop,
  MOBILE_WIZARD_LAST_STEP: mobileWizardLastStep,
};

interface MobileWizardStateProps {
  expanded: boolean;
  currentStep?: keyof typeof targetComponents;
  stepsToShow?: Array<keyof typeof targetComponents>;
  userSelections?: Record<string, any>;
}

interface MobileWizardDispatchProps {
  init: () => void;
  destroy: () => void;
  nextStep: () => void;
  previousStep: () => void;
  wixLogoClick: () => void;
  skipWizard: () => void;
  setStep: (nextStep: keyof typeof targetComponents) => void;
}

interface MobileWizardProps
  extends MobileWizardStateProps,
    MobileWizardDispatchProps {}

class MobileWizard extends React.Component<MobileWizardProps> {
  static displayName = 'mobileWizard';

  state = {
    currentStepIndex: 0,
  };

  onProgressBarStepClick = (stepIndex: number) => {
    const nextStep = this.props.stepsToShow[stepIndex];
    this.props.setStep(nextStep);
  };

  getCurrentStepComponent = () => {
    const currentStepComponent = targetComponents[this.props.currentStep];
    return currentStepComponent
      ? React.createElement(currentStepComponent, {})
      : undefined;
  };

  componentDidMount() {
    this.props.init();
  }

  componentWillUnmount() {
    this.props.destroy();
  }

  UNSAFE_componentWillReceiveProps(nextProps: MobileWizardProps) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/index-of
    const currentStepIndex = _.indexOf(
      nextProps.stepsToShow,
      nextProps.currentStep,
    );
    this.setState({ currentStepIndex });
  }

  shouldShowWizardActions = () => {
    return !(this.isFirstStep() || this.isLastStep());
  };

  shouldShowSkipButton = () => {
    return !(this.isFirstStep() || this.isLastStep());
  };

  isFirstStep = () => {
    return this.state.currentStepIndex === 0;
  };

  isLastStep = () => {
    return this.state.currentStepIndex === this.props.stepsToShow.length - 1;
  };

  render() {
    return (
      <div
        onContextMenu={browserUtil.preventDefaultRightClick}
        data-aid="mobile-wizard"
        className={cx(styles.mobileWizard, {
          [styles.mobileWizardExpanded]: this.props.expanded,
        })}
      >
        <div className={styles.mobileWizardTopBar}>
          <span
            onClick={() => {
              this.props.wixLogoClick();
            }}
            className={styles.topBarLeftWixLogo}
          >
            <symbols.symbol name="wixLogo" />
          </span>

          {this.shouldShowSkipButton() ? (
            <Button
              automationId="mobileWizard-top-bar-skip"
              key="mobile-wizard-top-bar-skip"
              onClick={() => {
                this.props.skipWizard();
              }}
              className={util.cx(
                'btn-sm btn-text',
                styles.mobileWizardTopBarSkip,
              )}
            >
              {translate('Mobile_Wizard_Welcome_Skip_Link')}
            </Button>
          ) : null}
        </div>
        <div className={styles.mobileWizardLeftHalf}>
          <div className={styles.mobileWizardInner}>
            <ProgressBar
              current={this.state.currentStepIndex}
              total={this.props.stepsToShow.length}
              onStepClick={this.onProgressBarStepClick}
            />

            <div className={styles.currentStepContainer}>
              {this.getCurrentStepComponent()}
            </div>

            {this.shouldShowWizardActions() ? (
              <Composites.ActionSetHorizontal
                key="alt"
                className={styles.mobileWizardActions}
              >
                <Button
                  onClick={() => {
                    this.props.previousStep();
                  }}
                  automationId="mobile-wizard-previous"
                  className="btn-confirm-secondary"
                >
                  <span>
                    {translate('Mobile_Wizard_Welcome_Previous_Button')}
                  </span>
                </Button>
                <Button
                  onClick={() => {
                    this.props.nextStep();
                  }}
                  automationId="mobile-wizard-next"
                >
                  <span>{translate('Mobile_Wizard_Welcome_Next_Button')}</span>

                  <svg width="5" height="9" viewBox="0 0 5 9">
                    <path
                      fill="#FFF"
                      fillRule="evenodd"
                      d="M0 0h1v1H0zm1 1h1v1H1zm1 1h1v1H2zm1 1h1v1H3zm1 1h1v1H4zM3 5h1v1H3zM2 6h1v1H2zM1 7h1v1H1zM0 8h1v1H0z"
                    />
                  </svg>
                </Button>
              </Composites.ActionSetHorizontal>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  state,
}: StateMapperArgs): MobileWizardStateProps => {
  const expanded =
    selectors.getMobileWizardOpeningState(state) ===
    MOBILE_WIZARD_OPENING_STATE.IN_PROGRESS;
  return {
    expanded,
    currentStep: selectors.getCurrentStep(state),
    stepsToShow: selectors.getStepsToShow(state),
    userSelections: selectors.getUserSelections(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch): MobileWizardDispatchProps => ({
  init() {
    dispatch(actions.buildWizardSteps());
  },
  destroy() {
    dispatch(actions.clearAfterMobileWizardCloses());
  },
  skipWizard() {
    dispatch(actions.skipWizard());
  },
  nextStep() {
    dispatch(actions.setNextStep());
  },
  previousStep() {
    dispatch(actions.setPreviousStep());
  },
  wixLogoClick() {
    dispatch(actions.wixLogoClick());
  },
  setStep(nextStep: keyof typeof targetComponents) {
    dispatch(actions.setStepFromProgressBar(nextStep));
  },
});

export default util.hoc.connect(
  util.hoc.STORES.STATE_ONLY,
  mapStateToProps,
  mapDispatchToProps,
)(MobileWizard);
